/**
 * Naive 3D hand-trajectory reconstruction.
 *
 * Pipeline:
 *   1. Group MetaWear samples by hand and split into the two streams
 *      that fusion mode emits (`quaternion`, `linearAcceleration`).
 *   2. For each `linearAcceleration` sample, find the most recent
 *      `quaternion` sample on the same hand (within the same connection
 *      event window — they arrive interleaved at 100 Hz each).
 *   3. Rotate body-frame linear acceleration into world frame with that
 *      quaternion.
 *   4. Double-integrate world-frame acceleration to velocity, then
 *      position. Anchor at (0, 0, 0) with zero initial velocity.
 *
 * **Drift correction (v3)**: two layers.
 *   1. Per-hand body-frame mean linAccel is subtracted from each sample
 *      before rotation. This removes the constant accelerometer bias
 *      the fusion module didn't fully cancel.
 *   2. ZUPT (Zero-velocity UPdaTe). When the body-frame
 *      `linearAcceleration` magnitude stays below a small threshold
 *      across a short rolling window, we treat the hand as stationary
 *      and reset world-frame velocity to zero. This bounds the
 *      remaining drift to whatever accumulates between rest periods,
 *      rather than letting it grow unbounded across the whole session.
 *
 * Together these are enough to surface the real motion envelope; the
 * tradeoff is that ZUPT can over-correct during slow steady motion
 * (mistaking it for rest) and under-correct if the hand never truly
 * settles. Tune `ZUPT_THRESHOLD_G` and `ZUPT_WINDOW_MS` if either
 * pattern shows up in real recordings.
 *
 * Only meaningful for sessions recorded in `fusionMode === 'host'`
 * (or the legacy `'imuplus'` firmware path); returns an empty array if
 * no quaternion samples are present.
 */

import type { SensorSample } from './types';

/** Conversion factor: linearAcceleration is reported in g (9.80665 m/s²). */
const G_TO_MPS2 = 9.80665;

/**
 * ZUPT (Zero-velocity UPdaTe) parameters.
 *
 * Stationary detection uses the **variance** of the bias-corrected
 * body-frame `linearAcceleration` magnitude across a rolling window
 * rather than the magnitude itself. A still hand reads a roughly
 * constant noise floor (variance ~ 0), whereas any real motion
 * produces fluctuating accel (variance grows with motion intensity).
 *
 * Magnitude-thresholded ZUPT was tried first and failed on the
 * 2026-05-23 R session: the right-hand sensor's noise-floor sat at
 * ~0.08 g (mean-subtracted) and the threshold had to be pushed so high
 * that real motion was misclassified as rest. Variance is robust to a
 * sensor's constant noise floor — both hands behave consistently with
 * the same threshold.
 *
 * 1e-3 g² is roughly the 75th-percentile window-variance on the right
 * hand at rest; tightening it under-fires (drift grows), loosening it
 * over-fires (slow motion gets quashed). 250 ms is short enough to
 * fire several times per procedure and long enough not to trigger on
 * fast direction reversals where instantaneous variance briefly dips.
 */
const ZUPT_VAR_THRESHOLD_G2 = 1e-3;
const ZUPT_WINDOW_MS = 250;
const ZUPT_MIN_SAMPLES = 5;

/** One point on a hand's reconstructed trajectory. */
export interface TrajPoint {
  /** Absolute host time of the sample, ms. */
  t: number;
  /** World-frame position in metres. */
  x: number;
  y: number;
  z: number;
}

/** A reconstructed trajectory for one hand. */
export interface HandTrajectory {
  /** `metawear-left` or `metawear-right` — keyed off the sample sensorId. */
  sensorId: string;
  points: TrajPoint[];
}

/**
 * Rotate a body-frame vector by a unit quaternion (w, x, y, z) to obtain
 * the corresponding world-frame vector. Uses the standard cross-product
 * formulation:
 *
 *   v' = v + 2 q_w (q_vec × v) + 2 q_vec × (q_vec × v)
 *
 * which is equivalent to q ⊗ (0, v) ⊗ q⁻¹ but avoids the full quaternion
 * multiplication.
 */
function rotateByQuat(
  qw: number, qx: number, qy: number, qz: number,
  vx: number, vy: number, vz: number,
): [number, number, number] {
  // t = 2 * (q_vec × v)
  const tx = 2 * (qy * vz - qz * vy);
  const ty = 2 * (qz * vx - qx * vz);
  const tz = 2 * (qx * vy - qy * vx);
  // v' = v + q_w * t + q_vec × t
  return [
    vx + qw * tx + (qy * tz - qz * ty),
    vy + qw * ty + (qz * tx - qx * tz),
    vz + qw * tz + (qx * ty - qy * tx),
  ];
}

/**
 * Bucket a flat sample stream by MetaWear hand, keeping only the two
 * fusion streams. Non-metawear sensors and other streams are dropped.
 */
function groupByHand(
  samples: SensorSample[],
): Record<string, { quat: SensorSample[]; lin: SensorSample[] }> {
  const handData: Record<
    string,
    { quat: SensorSample[]; lin: SensorSample[] }
  > = {};
  for (const s of samples) {
    if (!s.sensorId.startsWith('metawear-')) continue;
    if (s.stream !== 'quaternion' && s.stream !== 'linearAcceleration') continue;
    if (!handData[s.sensorId]) handData[s.sensorId] = { quat: [], lin: [] };
    if (s.stream === 'quaternion') handData[s.sensorId].quat.push(s);
    else handData[s.sensorId].lin.push(s);
  }
  return handData;
}

/**
 * Per-phase mean body-frame linear acceleration — the accelerometer-bias
 * residual the host-side Madgwick filter didn't fully cancel. Subtracting
 * this from each sample before rotation is a simple DC-removal step:
 * - Correct if the hand returns to rest on average within each phase (true
 *   mean acceleration over a stationary-ish phase is ~0 in body frame).
 * - Per-phase (not session-wide) because the quaternion drifts between
 *   phases on the noisy right-hand sensor — a session-wide mean leaves a
 *   step-shaped residual at the phase boundary that integrates into
 *   multi-metre drift (verified 2026-05-26: session-wide → 41 m right-hand
 *   drift; per-phase → 30 m).
 * Samples read from disk carry a `phase`; samples produced mid-session may
 * not — fall back to a single 'all' bucket so this is safe for either caller.
 */
function phaseMeanBias(
  lin: SensorSample[],
): Map<string, { x: number; y: number; z: number }> {
  const acc = new Map<
    string,
    { x: number; y: number; z: number; n: number }
  >();
  for (const s of lin) {
    const key = s.phase ?? 'all';
    let m = acc.get(key);
    if (!m) {
      m = { x: 0, y: 0, z: 0, n: 0 };
      acc.set(key, m);
    }
    m.x += s.values[0];
    m.y += s.values[1];
    m.z += s.values[2];
    m.n++;
  }
  const means = new Map<string, { x: number; y: number; z: number }>();
  for (const [key, m] of acc) {
    means.set(
      key,
      m.n > 0 ? { x: m.x / m.n, y: m.y / m.n, z: m.z / m.n } : { x: 0, y: 0, z: 0 },
    );
  }
  return means;
}

/**
 * Population variance of the |a_body| magnitudes in the ZUPT window. Using
 * variance (not the raw magnitude) makes stationary detection robust to a
 * sensor's constant noise floor — both hands trip the same threshold
 * despite different noise floors. Empty window → 0.
 */
function magVariance(window: readonly { mag: number }[]): number {
  if (window.length === 0) return 0;
  let sum = 0;
  for (const w of window) sum += w.mag;
  const mean = sum / window.length;
  let varSum = 0;
  for (const w of window) {
    const d = w.mag - mean;
    varSum += d * d;
  }
  return varSum / window.length;
}

/**
 * Compute reconstructed 3D trajectories for each MetaWear hand from a
 * flat sample stream. Samples may arrive in any order and contain any
 * sensorId / stream combination — only fusion-mode IMU samples are used.
 */
export function computeTrajectories(samples: SensorSample[]): HandTrajectory[] {
  const handData = groupByHand(samples);

  const result: HandTrajectory[] = [];
  for (const [sensorId, { quat, lin }] of Object.entries(handData)) {
    if (quat.length === 0 || lin.length === 0) continue;
    quat.sort((a, b) => a.hostTime - b.hostTime);
    lin.sort((a, b) => a.hostTime - b.hostTime);

    // Per-phase mean-linAccel bias removed before rotation — see phaseMeanBias.
    const phaseMeans = phaseMeanBias(lin);

    const points: TrajPoint[] = [];
    let vx = 0, vy = 0, vz = 0;
    let px = 0, py = 0, pz = 0;
    let qIdx = 0;
    let prevT: number | null = null;
    // Rolling window of (hostTime, |a_body|) for the ZUPT detector.
    // Trimmed by hostTime rather than count so the window is robust to
    // BLE rate variation.
    const zuptWindow: { t: number; mag: number }[] = [];

    for (const linSample of lin) {
      // Advance qIdx to the latest quaternion with hostTime <= the
      // linAccel sample. At 100 Hz × 2 streams the two streams arrive
      // tightly interleaved; this nearest-prior pairing is the simplest
      // bias-free approach.
      while (
        qIdx + 1 < quat.length &&
        quat[qIdx + 1].hostTime <= linSample.hostTime
      ) {
        qIdx++;
      }
      const q = quat[qIdx].values;
      const a = linSample.values;
      if (q.length < 4 || a.length < 3) continue;

      // Bias-corrected body-frame acceleration (still in g). The mean
      // is per-phase — see phaseMeans construction above.
      const m = phaseMeans.get(linSample.phase ?? 'all');
      if (!m) continue;
      const bax = a[0] - m.x;
      const bay = a[1] - m.y;
      const baz = a[2] - m.z;
      const bmag = Math.sqrt(bax * bax + bay * bay + baz * baz);

      // Maintain the ZUPT rolling window: append current magnitude,
      // drop anything older than ZUPT_WINDOW_MS. A window-max below
      // the threshold means the hand has been still for the whole
      // window — fire ZUPT.
      zuptWindow.push({ t: linSample.hostTime, mag: bmag });
      const windowStart = linSample.hostTime - ZUPT_WINDOW_MS;
      while (zuptWindow.length > 0 && zuptWindow[0].t < windowStart) {
        zuptWindow.shift();
      }

      // Rotate body-frame accel into world frame, scaled to m/s².
      const [awx, awy, awz] = rotateByQuat(
        q[0], q[1], q[2], q[3],
        bax * G_TO_MPS2,
        bay * G_TO_MPS2,
        baz * G_TO_MPS2,
      );

      if (prevT === null) {
        prevT = linSample.hostTime;
        points.push({ t: linSample.hostTime, x: 0, y: 0, z: 0 });
        continue;
      }
      const dt = (linSample.hostTime - prevT) / 1000;
      if (dt <= 0 || dt > 1) {
        // Clock went backwards, or a dropout left a >1 s gap — don't try
        // to integrate across the gap; anchor and continue.
        prevT = linSample.hostTime;
        continue;
      }

      // Fire ZUPT when the rolling window's variance of |a_body|
      // drops below the threshold (and the window is warm). Using
      // variance instead of magnitude makes the detector robust to
      // per-sensor noise-floor differences — both hands trip the same
      // threshold despite the right hand sitting at ~0.08 g vs the
      // left at ~0.04 g (see comments on ZUPT_VAR_THRESHOLD_G2).
      const windowWarm =
        zuptWindow.length >= ZUPT_MIN_SAMPLES &&
        linSample.hostTime - zuptWindow[0].t >= ZUPT_WINDOW_MS * 0.8;
      let isStationary = false;
      if (windowWarm) {
        isStationary = magVariance(zuptWindow) < ZUPT_VAR_THRESHOLD_G2;
      }
      if (isStationary) {
        vx = 0; vy = 0; vz = 0;
      }

      // Trapezoidal velocity + midpoint position. Standard symplectic
      // step for a kinematic chain.
      const prevVx = vx, prevVy = vy, prevVz = vz;
      vx += awx * dt;
      vy += awy * dt;
      vz += awz * dt;
      px += 0.5 * (prevVx + vx) * dt;
      py += 0.5 * (prevVy + vy) * dt;
      pz += 0.5 * (prevVz + vz) * dt;
      points.push({ t: linSample.hostTime, x: px, y: py, z: pz });
      prevT = linSample.hostTime;
    }

    result.push({ sensorId, points });
  }
  return result;
}
